import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import type { Material, CreateMaterialInput } from '../../api/materials.api';

interface Props {
  open: boolean;
  initial?: Material | null;
  onClose: () => void;
  onSubmit: (input: CreateMaterialInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateMaterialInput = { materialDescription: '', unit: '', status: 'active' };

export function MaterialFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateMaterialInput>(empty);

  useEffect(() => {
    setForm(
      initial
        ? {
            materialDescription: initial.materialDescription,
            category: initial.category ?? undefined,
            unit: initial.unit,
            hsnCode: initial.hsnCode ?? undefined,
            status: initial.status,
          }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateMaterialInput>(k: K, v: CreateMaterialInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Material — ${initial?.materialCode}` : 'Create Material'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Description" value={form.materialDescription} onChange={(e) => set('materialDescription', e.target.value)} required fullWidth multiline rows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label="Category" value={form.category ?? ''} onChange={(e) => set('category', e.target.value)} fullWidth />
            <TextField label="Unit" value={form.unit} onChange={(e) => set('unit', e.target.value)} required fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="HSN Code" value={form.hsnCode ?? ''} onChange={(e) => set('hsnCode', e.target.value)} fullWidth />
            <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as CreateMaterialInput['status'])} fullWidth>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
